import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from './useInView';
import {
  HiOutlinePhone,
  HiOutlineMail,
  HiOutlineLocationMarker,
  HiOutlineClock,
} from 'react-icons/hi';

const contactInfo = [
  {
    icon: HiOutlinePhone,
    title: 'Phone',
    detail: '+971 50 501 9175',
    sub: 'Mon–Sat, 9AM – 7PM',
  },
  {
    icon: HiOutlineMail,
    title: 'Email',
    detail: 'drnoorakhalilibrahim@gmail.com',
    sub: 'We respond within 24 hours',
  },
  {
    icon: HiOutlineLocationMarker,
    title: 'Location',
    detail: 'Medical Center',
    sub: 'Dubai, UAE and Timisoara, Romania',
  },
  {
    icon: HiOutlineClock,
    title: 'Working Hours',
    detail: 'Mon – Sat: 9AM – 7PM',
    sub: 'Sunday: By Appointment',
  },
];

export default function Contact() {
  const [ref, inView] = useInView({ threshold: 0.15 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch('/api/consultations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch {
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" ref={ref} className="section-padding bg-cream">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="gold-line" />
            <span className="text-xs tracking-[0.3em] uppercase text-gold-600 font-medium">
              Get in Touch
            </span>
            <span className="gold-line" />
          </div>
          <h2 className="heading-lg mb-6">
            Begin Your{' '}
            <span className="italic text-gold-500">Journey</span>
          </h2>
          <p className="body-lg">
            Ready to enhance your natural beauty? Book a consultation with
            Dr. Noora Ibrahim and take the first step toward your aesthetic goals.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-2 space-y-8"
          >
            {contactInfo.map((item) => (
              <div key={item.title} className="flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                  <item.icon className="w-5 h-5 text-gold-500" />
                </div>
                <div>
                  <p className="font-medium text-charcoal-900 text-sm mb-0.5">
                    {item.title}
                  </p>
                  <p className="text-charcoal-700 text-sm">{item.detail}</p>
                  <p className="text-xs text-charcoal-400 mt-0.5">{item.sub}</p>
                </div>
              </div>
            ))}

            {/* Social links placeholder */}
            <div className="pt-6 border-t border-charcoal-100">
              <p className="text-xs tracking-[0.2em] uppercase text-charcoal-400 mb-4">
                Follow Us
              </p>
             <div className="flex gap-3">
  {[
    { name: 'Instagram', url: 'https://www.instagram.com/dr_noora_asthetics_' },
    { name: 'Facebook', url: '#' },
  ].map((social) => (
    <a
      key={social.name}
      href={social.url}
      target="_blank"
      rel="noopener noreferrer"
      className="px-4 py-2 rounded-full bg-white text-xs text-charcoal-600 tracking-wider uppercase shadow-sm hover:shadow-md transition-shadow cursor-pointer"
    >
      {social.name}
    </a>
  ))}
</div>
            </div>
          </motion.div>

          {/* Contact form */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="lg:col-span-3 bg-white rounded-2xl p-8 md:p-10 shadow-sm"
          >
            <div className="grid md:grid-cols-2 gap-5 mb-5">
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-500 mb-2 font-medium">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-charcoal-100 bg-cream/50 text-sm text-charcoal-900 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-all"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-500 mb-2 font-medium">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-charcoal-100 bg-cream/50 text-sm text-charcoal-900 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-all"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5 mb-5">
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-500 mb-2 font-medium">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-charcoal-100 bg-cream/50 text-sm text-charcoal-900 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-all"
                  placeholder="+971 XX XXX XXXX"
                />
              </div>
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-500 mb-2 font-medium">
                  Treatment Interest
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-charcoal-100 bg-cream/50 text-sm text-charcoal-900 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-all appearance-none"
                >
                  <option value="">Select a treatment</option>
                  <option value="botox">Advanced Botox Therapy</option>
                  <option value="fillers">Dermal Fillers</option>
                  <option value="fat-removal">Fat Removal</option>
                  <option value="undereye">Under-Eye Treatment</option>
                  <option value="mesotherapy">Mesotherapy</option>
                  <option value="exosomes">Exosomes Therapy</option>
                  <option value="salmon-dna">Salmon DNA Therapy</option>
                  <option value="skin">Skin Rejuvenation</option>
                  <option value="other">Other / Consultation</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-xs tracking-wider uppercase text-charcoal-500 mb-2 font-medium">
                Message
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-3 rounded-lg border border-charcoal-100 bg-cream/50 text-sm text-charcoal-900 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-all resize-none"
                placeholder="Tell us about your aesthetic goals..."
              />
            </div>

            {submitted ? (
              <div className="text-center py-4">
                <p className="text-gold-600 font-medium text-sm mb-1">Thank you for your inquiry!</p>
                <p className="text-charcoal-400 text-xs">We will contact you within 24 hours.</p>
              </div>
            ) : (
              <>
                <button type="submit" disabled={submitting} className="btn-primary w-full disabled:opacity-50">
                  {submitting ? 'Sending...' : 'Request Consultation'}
                </button>
                <p className="text-[11px] text-charcoal-400 text-center mt-4">
                  Your information is kept strictly confidential. We will respond
                  within 24 hours.
                </p>
              </>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
}
